import React, { useState, useRef, useEffect } from 'react';
import {
    FileProtectOutlined,
    EyeOutlined,
    CopyOutlined,
    SendOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    FilePdfOutlined,
    DeleteOutlined,
    PlusOutlined
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { Table, Button, Input, Tag, Space, Modal, Typography, Tooltip, message, List, Empty, Card } from 'antd';
import { useSelector } from 'react-redux';
import { stateGlobal } from '../../../interfaces/Global';
import { usePropostas } from './usePropostas';
import { IProposta, IPropostaListItem, ISlidePropostaData } from '../interface';
import { format } from 'date-fns';
import HeaderAcoesTabela from '../../../components/HeaderAcoesTabela';
import { VisualizadorPropostaModal, PropostaPrintDocument } from '../components';
import { gerarPdfPropostaDireto } from '../utils/gerarPdfProposta';
import PropostaService from '../../../services/http/services/Propostas/PropostaService';
import { useGetColumnSearchPropsTypeString } from '../../../hooks/FilterTable/objString';
import { useGetColumnSearchPropsData } from '../../../hooks/FilterTable/date';
import { convertMoedaBRDoJSComExtamente2CasasDecimais } from '../../../services/Conversores';

const { Title, Text } = Typography;

const formatarData = (data?: string | Date | null) => {
    if (!data) return '-';
    try {
        const d = new Date(data);
        if (isNaN(d.getTime())) return '-';
        return format(d, 'dd/MM/yyyy HH:mm');
    } catch {
        return '-';
    }
};

const ListagemPropostas: React.FC = () => {
    const history = useHistory();
    const isDark = useSelector((state: stateGlobal) => state?.global?.theme === 'dark');
    const {
        propostas,
        loading,
        filters,
        modalEnvioVisible,
        propostaSelecionada,
        statusEnvioDesejado,
        enviadoPara,
        novoEnvioPara,
        novoEnvioObs,
        loadingEnvio,
        loadingRegistrarEnvio,
        previewVisible,
        propostaVisualizar,
        getPropostaSlideData,
        handleVisualizar,
        handleClosePreview,
        setStatusEnvioDesejado,
        setEnviadoPara,
        setNovoEnvioPara,
        setNovoEnvioObs,
        setModalEnvioVisible,
        handleSearch,
        handleFilterStatus,
        handleDuplicar,
        handleExcluir,
        handleOpenModalEnvio,
        handleConfirmarStatusEnvio,
        handleRegistrarNovoEnvio,
        handleExcluirEnvio,
        handleNovaProposta,
        handleEditarProposta,
        carregarPropostas
    } = usePropostas();

    const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
    const [propostaParaDownload, setPropostaParaDownload] = useState<ISlidePropostaData | null>(null);
    const [gerandoPdf, setGerandoPdf] = useState<boolean>(false);
    const [filteredCount, setFilteredCount] = useState<number | null>(null);
    const [isMobile, setIsMobile] = useState<boolean>(() => typeof window !== 'undefined' && window.innerWidth <= 768);
    const printListRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const searchPropsMunicipio = useGetColumnSearchPropsTypeString('cidade_nome');
    const searchPropsPlano = useGetColumnSearchPropsTypeString('plano_nome');
    const searchPropsGerador = useGetColumnSearchPropsTypeString('gerado_por_nome');
    const searchPropsData = useGetColumnSearchPropsData('gerado_em');

    useEffect(() => {
        setFilteredCount(null);
    }, [propostas]);

    const selectedProposta = propostas.find(p => p.id === selectedRowKeys[0]);

    const isEnviada = Boolean(selectedProposta?.enviado || selectedProposta?.status === 'enviada');

    const handleBaixarPdf = async (proposta: IPropostaListItem | IProposta) => {
        if (proposta.gerada === false) {
            message.warning('Esta proposta é uma cópia duplicada. Por favor, edite e salve a proposta antes de baixar o PDF.');
            return;
        }
        setGerandoPdf(true);
        message.loading({ content: 'Carregando dados da proposta...', key: 'pdf_list_gen', duration: 0 });

        try {
            const fullProposta = await PropostaService.getPropostaById(proposta.id);
            if (!fullProposta) {
                message.error({ content: 'Proposta não encontrada', key: 'pdf_list_gen', duration: 3 });
                setGerandoPdf(false);
                return;
            }

            const slideData = getPropostaSlideData(fullProposta);
            setPropostaParaDownload(slideData);
            const muni = (fullProposta.cidade_nome || 'Municipio').replace(/\s+/g, '_');
            const uf = (fullProposta.cidade_uf || 'UF').toUpperCase();
            const title = `Proposta_Comercial_${muni}_${uf}_GovFacil`;

            message.loading({ content: 'Gerando PDF da proposta comercial...', key: 'pdf_list_gen', duration: 0 });

            setTimeout(async () => {
                try {
                    if (printListRef.current) {
                        await gerarPdfPropostaDireto({
                            containerElement: printListRef.current,
                            documentTitle: title,
                            onProgress: (current, total) => {
                                message.loading({ content: `Processando slide ${current} de ${total}...`, key: 'pdf_list_gen', duration: 0 });
                            }
                        });
                        message.success({ content: 'PDF baixado com sucesso!', key: 'pdf_list_gen', duration: 3 });
                    } else {
                        message.destroy('pdf_list_gen');
                    }
                } catch (error) {
                    console.error(error);
                    message.error({ content: 'Erro ao gerar PDF da proposta', key: 'pdf_list_gen', duration: 3 });
                } finally {
                    setGerandoPdf(false);
                }
            }, 300);
        } catch (error) {
            console.error(error);
            message.error({ content: 'Erro ao carregar proposta', key: 'pdf_list_gen', duration: 3 });
            setGerandoPdf(false);
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 65,
            align: 'center' as const
        },
        {
            title: 'Município / UF',
            dataIndex: 'cidade_nome',
            key: 'cidade_nome',
            ...searchPropsMunicipio,
            sorter: (a: IPropostaListItem, b: IPropostaListItem) => (a.cidade_nome || '').localeCompare(b.cidade_nome || ''),
            showSorterTooltip: false,
            render: (_: any, record: IPropostaListItem) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <Text strong>{record.cidade_nome}{record.cidade_uf ? ` - ${record.cidade_uf}` : ''}</Text>
                    {record.gerada === false && (
                        <Tooltip title="Proposta duplicada pendente de edição e salvamento antes de liberar o download do PDF">
                            <Tag color="orange" style={{ margin: 0, fontSize: '11px', borderRadius: '10px', fontWeight: 500 }}>
                                Duplicada
                            </Tag>
                        </Tooltip>
                    )}
                </div>
            )
        },
        {
            title: 'Plano',
            dataIndex: 'plano_nome',
            key: 'plano_nome',
            ...searchPropsPlano,
            sorter: (a: IPropostaListItem, b: IPropostaListItem) => (a.plano_nome || '').localeCompare(b.plano_nome || ''),
            showSorterTooltip: false,
            render: (planoNome: string, record: IPropostaListItem) => (
                <div>
                    <Text>{planoNome || 'Plano Personalizado'}</Text>
                </div>
            )
        },
        {
            title: 'Qtd. Licenças / Horas',
            dataIndex: 'quantidade_licencas',
            key: 'quantidade_licencas',
            sorter: (a: IPropostaListItem, b: IPropostaListItem) => (a.quantidade_licencas || 0) - (b.quantidade_licencas || 0),
            showSorterTooltip: false,
            render: (_: any, record: IPropostaListItem) => (
                <div>
                    <div /*style={{ fontSize: '12px'}}*/>
                        {record.quantidade_licencas} licença(s) {record.contratacao_horas && record.quantidade_horas ? ` • ${record.quantidade_horas}h` : ''}
                    </div>
                </div>
            )
        },
        {
            title: 'Gerado Por',
            dataIndex: 'gerado_por_nome',
            key: 'gerado_por_nome',
            ...searchPropsGerador,
            sorter: (a: IPropostaListItem, b: IPropostaListItem) => {
                const aNome = a.gerada === false ? '' : (a.gerado_por_nome || '');
                const bNome = b.gerada === false ? '' : (b.gerado_por_nome || '');
                return aNome.localeCompare(bNome);
            },
            showSorterTooltip: false,
            render: (nome: string, record: IPropostaListItem) => (
                <div>
                    <div>{record.gerada === false ? '--' : (nome || '-')}</div>
                </div>
            )
        },
        {
            title: 'Gerado Em',
            dataIndex: 'gerado_em',
            key: 'gerado_em',
            ...searchPropsData,
            sorter: (a: IPropostaListItem, b: IPropostaListItem) => {
                const aTime = a.gerada === false || !a.gerado_em ? 0 : new Date(a.gerado_em).getTime();
                const bTime = b.gerada === false || !b.gerado_em ? 0 : new Date(b.gerado_em).getTime();
                return aTime - bTime;
            },
            showSorterTooltip: false,
            render: (_: any, record: IPropostaListItem) => (
                <div>
                    <div>{record.gerada === false || !record.gerado_em ? '--' : formatarData(record.gerado_em)}</div>
                </div>
            )
        },
        {
            title: 'Valor Total (R$)',
            dataIndex: 'valor_total_geral',
            key: 'valor_total_geral',
            align: 'right' as const,
            sorter: (a: IPropostaListItem, b: IPropostaListItem) => Number(a.valor_total_geral || 0) - Number(b.valor_total_geral || 0),
            showSorterTooltip: false,
            render: (valor: number | string) => (
                <Text strong style={{ fontSize: '15px' }}>
                    {convertMoedaBRDoJSComExtamente2CasasDecimais(valor)}
                </Text>
            )
        },
        {
            title: 'Status Envio',
            dataIndex: 'status',
            key: 'status',
            align: 'center' as const,
            filters: [
                { text: 'Enviada', value: 'enviada' },
                { text: 'Não Enviada', value: 'nao_enviada' }
            ],
            onFilter: (value: any, record: IPropostaListItem) => {
                const isEnviada = record.status === 'enviada' || record.enviado;
                return value === 'enviada' ? isEnviada : !isEnviada;
            },
            render: (status: string, record: IPropostaListItem) => {
                const isEnviada = status === 'enviada' || record.enviado;
                const naoGerada = record.gerada === false;
                const totalEnvios = record.envios?.length || (isEnviada ? 1 : 0);

                let tooltipTitle = 'Clique para gerenciar o histórico de envios';
                if (naoGerada) {
                    tooltipTitle = 'Proposta não salva. Edite e salve a proposta antes de registrar o envio.';
                } else if (isEnviada) {
                    tooltipTitle = totalEnvios > 1
                        ? `Enviada para ${totalEnvios} destinatários. Clique para ver o histórico de envios.`
                        : `Enviada para: ${record.enviado_para || 'Destinatário'} (${formatarData(record.enviado_em)}). Clique para gerenciar.`;
                }

                return (
                    <Tooltip title={tooltipTitle}>
                        <Tag
                            icon={isEnviada ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                            color={isEnviada ? 'success' : 'warning'}
                            style={{
                                padding: '3px 10px',
                                fontSize: '12px',
                                borderRadius: '12px',
                                cursor: naoGerada ? 'not-allowed' : 'pointer',
                                fontWeight: 500,
                                opacity: naoGerada ? 0.75 : 1
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (naoGerada) {
                                    message.warning('Esta proposta é uma cópia duplicada. Por favor, edite e salve a proposta antes de registrar o envio.');
                                    return;
                                }
                                handleOpenModalEnvio(record);
                            }}
                        >
                            {isEnviada ? 'Enviada' : 'Não Enviada'}
                        </Tag>
                    </Tooltip>
                );
            }
        }
    ];

    return (
        <div style={{ padding: isMobile ? '12px 8px' : '24px' }}>
            <div style={{ marginBottom: 16 }}>
                <Title level={3} style={{ margin: 0, gap: 6 }}>
                    <FileProtectOutlined /> Propostas Comerciais
                </Title>
                <Text type="secondary">Gerencie, acompanhe e envie propostas para municípios e clientes.</Text>
            </div>

            {/* Barra de Ações Padronizada */}
            <HeaderAcoesTabela
                showAdd={true}
                addText="Nova Proposta"
                disabledAdd={Boolean(selectedProposta)}
                onAdd={handleNovaProposta}

                showEdit={true}
                editText="Editar"
                disabledEdit={selectedRowKeys.length !== 1 || (selectedProposta ? selectedProposta.gerada === true : false)}
                onEdit={() => {
                    if (selectedRowKeys.length === 1) {
                        handleEditarProposta(selectedRowKeys[0]);
                    }
                }}

                showStatus={false}
                statusPlaceholder="Status de Envio"
                statusValue={filters.status || 'todos'}
                statusOptions={[
                    { label: 'Todos os Status', value: 'todos' },
                    { label: 'Enviadas', value: 'enviada' },
                    { label: 'Não Enviadas', value: 'nao_enviada' }
                ]}
                onStatusChange={(val) => handleFilterStatus(val === 'todos' ? undefined : val)}

                showCounter={true}
                filteredCount={filteredCount !== null ? filteredCount : propostas.length}
                totalCount={propostas.length}

                extraActions={
                    <Space wrap>
                        <Button
                            icon={<EyeOutlined />}
                            disabled={selectedRowKeys.length !== 1}
                            onClick={() => {
                                if (selectedProposta) {
                                    handleVisualizar(selectedProposta);
                                }
                            }}
                            style={{ marginBottom: 5, color: selectedRowKeys.length === 1 ? '#0066b2' : undefined, borderColor: selectedRowKeys.length === 1 ? '#0066b2' : undefined }}
                        >
                            Visualizar
                        </Button>
                        <Tooltip
                            title={
                                selectedProposta && selectedProposta.gerada === false
                                    ? 'Proposta duplicada. Edite e salve a proposta para liberar o download do PDF.'
                                    : undefined
                            }
                        >
                            <span>
                                <Button
                                    danger
                                    type="primary"
                                    icon={<FilePdfOutlined />}
                                    disabled={selectedRowKeys.length !== 1 || gerandoPdf || (selectedProposta ? selectedProposta.gerada === false : false)}
                                    loading={gerandoPdf}
                                    onClick={() => {
                                        if (selectedProposta) {
                                            handleBaixarPdf(selectedProposta);
                                        }
                                    }}
                                    style={{ marginBottom: 5, fontWeight: 500 }}
                                >
                                    {gerandoPdf ? 'Gerando PDF...' : 'Baixar PDF'}
                                </Button>
                            </span>
                        </Tooltip>
                        <Button
                            icon={<CopyOutlined />}
                            disabled={selectedRowKeys.length !== 1 || (selectedProposta ? selectedProposta.gerada === false : false)}
                            onClick={() => {
                                if (selectedProposta) {
                                    handleDuplicar(selectedProposta.id);
                                }
                            }}
                            style={{ marginBottom: 5 }}
                        >
                            Duplicar
                        </Button>
                        <Button
                            icon={<SendOutlined />}
                            disabled={selectedRowKeys.length !== 1 || (selectedProposta ? selectedProposta.gerada === false : false)}
                            onClick={() => {
                                if (selectedProposta) {
                                    handleOpenModalEnvio(selectedProposta);
                                }
                            }}
                            style={{ marginBottom: 5 }}
                        >
                            Status Envio
                        </Button>
                        <Tooltip
                            title={
                                !selectedProposta
                                    ? undefined
                                    : isEnviada
                                        ? 'Propostas já enviadas não podem ser excluídas.'
                                        : 'Excluir proposta selecionada'
                            }
                        >
                            <span>
                                <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    disabled={selectedRowKeys.length !== 1 || isEnviada}
                                    onClick={() => {
                                        if (selectedProposta && !isEnviada) {
                                            handleExcluir(selectedProposta.id);
                                        }
                                    }}
                                    style={{ marginBottom: 5 }}
                                >
                                    Excluir
                                </Button>
                            </span>
                        </Tooltip>
                        {/* <Button
                            icon={<SettingOutlined />}
                            onClick={() => history.push('/propostas-comerciais/configuracoes')}
                            style={{ marginBottom: 5 }}
                        >
                            Configurações
                        </Button> */}
                    </Space>
                }
            />

            {/* Tabela de Propostas com Seleção de Linha */}
            <Table
                columns={columns}
                dataSource={propostas}
                rowKey="id"
                loading={loading}
                scroll={{ x: 880 }}
                rowSelection={{
                    type: 'checkbox',
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys as number[])
                }}
                onChange={(pagination, filters, sorter, extra) => {
                    setFilteredCount(extra.currentDataSource.length);
                }}
                onRow={(record) => ({
                    onClick: () => {
                        setSelectedRowKeys(prev =>
                            prev.includes(record.id) ? [] : [record.id]
                        );
                    },
                    onDoubleClick: () => {
                        handleVisualizar(record);
                    },
                    style: { cursor: 'pointer' }
                })}
                pagination={{
                    pageSize: 10,
                    showTotal: (total) => `Total de ${total} proposta(s)`
                }}
            />

            {/* Modal de Envio e Histórico Completo de Envios */}
            <Modal
                title={
                    <Space align="center" style={{ fontSize: '16px', fontWeight: 600 }}>
                        <SendOutlined style={{ color: '#0066b2' }} />
                        <span>
                            Histórico e Registro de Envios ({propostaSelecionada?.cidade_nome}{propostaSelecionada?.cidade_uf ? ` - ${propostaSelecionada?.cidade_uf}` : ''})
                        </span>
                    </Space>
                }
                visible={modalEnvioVisible}
                onCancel={() => setModalEnvioVisible(false)}
                footer={[
                    <Button key="fechar" type="primary" onClick={() => setModalEnvioVisible(false)}>
                        Fechar
                    </Button>
                ]}
                width={isMobile ? '96vw' : 680}
                style={{ top: isMobile ? 12 : 24, maxWidth: '100vw' }}
                bodyStyle={{ maxHeight: 'calc(100vh - 140px)', overflowY: 'auto', padding: isMobile ? '12px 8px' : '20px 24px' }}
                destroyOnClose
            >
                {/* 1. Formulário para Registrar Novo Envio */}
                <Card
                    type="inner"
                    title={<Text strong><PlusOutlined style={{ marginRight: 6, color: '#2e7d32' }} />Registrar Novo Envio</Text>}
                    style={{
                        marginBottom: 20,
                        borderRadius: 8,
                        background: isDark ? '#1f1f1f' : '#fcfcfc',
                        border: isDark ? '1px solid #303030' : '1px solid #e8e8e8'
                    }}
                >
                    <div style={{ marginBottom: 12 }}>
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>
                            Destinatário / E-mail / Órgão <span style={{ color: 'red' }}>*</span>
                        </Text>
                        <Input
                            placeholder="Ex: Prefeito João Silva / pref@cidade.gov.br / Gabinete"
                            value={novoEnvioPara}
                            onChange={(e) => setNovoEnvioPara(e.target.value)}
                            onPressEnter={handleRegistrarNovoEnvio}
                            disabled={loadingRegistrarEnvio}
                        />
                    </div>

                    <div style={{ marginBottom: 14 }}>
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>
                            Observação (opcional):
                        </Text>
                        <Input
                            placeholder="Ex: Enviado por e-mail oficial com proposta em anexo"
                            value={novoEnvioObs}
                            onChange={(e) => setNovoEnvioObs(e.target.value)}
                            onPressEnter={handleRegistrarNovoEnvio}
                            disabled={loadingRegistrarEnvio}
                        />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            loading={loadingRegistrarEnvio}
                            onClick={handleRegistrarNovoEnvio}
                            style={{
                                background: '#2e7d32',
                                borderColor: '#2e7d32',
                                fontWeight: 500,
                                borderRadius: 6
                            }}
                        >
                            Salvar Envio
                        </Button>
                    </div>
                </Card>

                {/* 2. Histórico de Envios */}
                <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text strong style={{ fontSize: 14 }}>
                        Envios Realizados ({propostaSelecionada?.envios?.length || 0}):
                    </Text>
                </div>

                {propostaSelecionada?.envios && propostaSelecionada.envios.length > 0 ? (
                    <List
                        itemLayout="horizontal"
                        dataSource={propostaSelecionada.envios}
                        style={{
                            maxHeight: 280,
                            overflowY: 'auto',
                            paddingRight: 4
                        }}
                        renderItem={(envio) => (
                            <List.Item
                                style={{
                                    padding: '12px 14px',
                                    marginBottom: 8,
                                    borderRadius: 6,
                                    background: isDark ? '#141414' : '#f5f5f5',
                                    border: isDark ? '1px solid #303030' : '1px solid #e8e8e8'
                                }}
                                actions={[
                                    <Tooltip title="Excluir este envio" key="delete">
                                        <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleExcluirEnvio(envio.id)}
                                        />
                                    </Tooltip>
                                ]}
                            >
                                <List.Item.Meta
                                    avatar={
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                background: isDark ? 'rgba(82, 196, 26, 0.15)' : '#f6ffed',
                                                border: isDark ? '1px solid #274916' : '1px solid #b7eb8f',
                                                color: '#52c41a',
                                                fontSize: 18,
                                                flexShrink: 0
                                            }}
                                        >
                                            <CheckCircleOutlined />
                                        </div>
                                    }
                                    title={
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                            <Text strong style={{ fontSize: 13 }}>{envio.enviado_para}</Text>
                                        </div>
                                    }
                                    description={
                                        <div>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Enviado em {formatarData(envio.enviado_em)} por {envio.enviado_por_nome || envio.enviado_por?.nome || 'Usuário'}
                                            </Text>
                                            {envio.observacao && (
                                                <div style={{ marginTop: 2 }}>
                                                    <Text type="secondary" italic style={{ fontSize: 11 }}>
                                                        Obs: {envio.observacao}
                                                    </Text>
                                                </div>
                                            )}
                                        </div>
                                    }
                                />
                            </List.Item>
                        )}
                    />
                ) : (
                    <div style={{
                        padding: '24px 0',
                        background: isDark ? '#141414' : '#fafafa',
                        borderRadius: 6,
                        border: isDark ? '1px dashed #303030' : '1px dashed #d9d9d9'
                    }}>
                        <Empty
                            description="Nenhum envio registrado para esta proposta ainda."
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    </div>
                )}
            </Modal>

            {/* Modal de Pré-visualização Interativa dos Slides */}
            <VisualizadorPropostaModal
                visible={previewVisible}
                onClose={handleClosePreview}
                data={getPropostaSlideData(propostaVisualizar)}
                podeBaixarPdf={propostaVisualizar ? propostaVisualizar.gerada !== false : true}
            />

            {/* Documento Oculto para Impressão Direta do PDF */}
            <PropostaPrintDocument
                ref={printListRef}
                data={propostaParaDownload}
            />
        </div>
    );
};

export default ListagemPropostas;
